import { type Observable } from '@legendapp/state';
import { createPersistedStore, createStore } from '@kidslabs/module-core';
import { type CartItemInterface, type Cart, CurrencyEnum } from '../types/front.graphql';
// import type {SyncedGetParams} from '@legendapp/state/sync';
// import {CartSynchronizationService} from '../services/cart-synchronization.service';
import { calculateTotalItems } from '../helpers/cart-item.helpers';

// ============================================================================
// FAST CART TOTALS TYPE - Server-computed cart totals (Magento quote totals)
// ============================================================================

export interface FastCartTotalsData {
    cart_id?: string | null;
    coupon_code?: string | null;
    currency_code?: string | null;
    discount_amount?: number | null;
    grand_total?: number | null;
    is_virtual?: boolean | null;
    items_count?: number | null;
    items_qty?: number | null;
    shipping_amount?: number | null;
    subtotal?: number | null;
    subtotal_with_discount?: number | null;
    tax_amount?: number | null;
}

// ============================================================================
// PERSISTENT CART STORE - Data that should persist across app restarts
// ============================================================================

export interface AvailabilityItem {
    isSalable: boolean;
    loading: boolean;
    availableQuantity: number | null;
    errors: Array<{ code: string; message: string }> | null;
}

interface PersistentCartStore {
    // Cart identification
    cartId: string;

    // === SERVER CART DATA ===
    cartData?: Cart;

    // === CONVENIENCE PROPERTIES ===
    // Computed from cartData for easy access
    items: CartItemInterface[];
    totalItems: number;

    // === TIMESTAMP TRACKING ===
    lastFetchedAt?: number; // Unix timestamp for staleness check
    lastSyncedAt?: number; // Last sync timestamp for multi-device
    availableItems: Record<string, AvailabilityItem>;

    /** Server-computed cart totals (Magento quote totals).
     *  Written by useCheckoutTotalsManager, read by useFastCartTotal. */
    fastCartTotals?: FastCartTotalsData;
}

// ============================================================================
// TRANSIENT CART STORE - Session data that should NOT persist
// ============================================================================

interface TransientCartStore {
    // Loading states (reset to false on app start)
    isCartLoading: boolean;
    isFetchingCart: boolean;
    isCartSetupLoading: boolean;
    /** Loading state for fast cart totals fetch */
    fastCartTotalsLoading: boolean;

    // Provider lifecycle flags (reset on store code change / logout)
    /** Whether customer cart has been fetched after authentication */
    customerCartLoaded: boolean;
    /** Whether cart initialization (reconciliation + first fetch) is complete */
    cartInitialized: boolean;
}

// Staleness configuration
export const CART_STALE_TIME = 5 * 60 * 1000; // 5 minutes

// ============================================================================
// PERSISTENT STORE - With automatic IndexedDB persistence
// ============================================================================

/**
 * Initial state for the persistent cart store.
 * Exported so resetCart can use cartStore$.set(CART_INITIAL_STATE)
 * which properly triggers Legend-State IndexedDB persistence cleanup.
 */
export const CART_INITIAL_STATE: PersistentCartStore = {
    cartId: '',
    cartData: undefined,
    items: [],
    totalItems: 0,
    lastFetchedAt: undefined,
    lastSyncedAt: undefined,
    availableItems: {},
    fastCartTotals: undefined,
};

/**
 * Cart persistent store with automatic IndexedDB persistence
 *
 * ✅ Auto-registers as 'cart'
 * ✅ No manual SSR checks needed
 * ✅ Automatic persistence on client-side
 * ✅ Data survives page refreshes
 */
export const cartStore$: Observable<PersistentCartStore> = createPersistedStore<PersistentCartStore>(
    'cart',
    CART_INITIAL_STATE
);

// ============================================================================
// TRANSIENT STORE - NO persistence, always starts fresh
// ============================================================================

/**
 * Cart transient store (loading states)
 *
 * ✅ No persistence - resets on page refresh
 * ✅ For temporary UI state only
 */
export const cartTransientStore$: Observable<TransientCartStore> = createStore<TransientCartStore>({
    isCartLoading: false,
    isFetchingCart: false,
    isCartSetupLoading: false,
    fastCartTotalsLoading: false,
    customerCartLoaded: false,
    cartInitialized: false,
});

// ============================================================================
// COMPUTED PROPERTIES - Unified access layer
// ============================================================================

export const cartComputed = {
    // Get total number of items (sum of all quantities)
    getTotalItems: () => {
        const items = cartStore$.items.peek();
        const filteredItems = items?.filter(item => !!item);
        return calculateTotalItems(filteredItems);
    },

    // Get total number of unique products
    getTotalUniqueItems: () => {
        return cartStore$.items.get()?.length;
    },

    // Get total value
    getTotalValue: () => {
        return (
            cartStore$.cartData.prices?.grand_total.get() || {
                value: 0,
                currency: CurrencyEnum.Vnd,
                __typename: 'Money',
            }
        );
    },

    // Check if cart is empty
    isEmpty: () => {
        return cartStore$.items.get()?.length === 0;
    },

    // Check if any loading is active
    isAnyLoading: () => {
        return (
            cartTransientStore$.isCartLoading.get() ||
            cartTransientStore$.isFetchingCart.get() ||
            cartTransientStore$.isCartSetupLoading.get()
        );
    },

    // Get specific loading state
    isCartActionsLoading: () => cartTransientStore$.isCartLoading.get(),
    isFetchingCart: () => cartTransientStore$.isFetchingCart.get(),
    isCartSetupLoading: () => cartTransientStore$.isCartSetupLoading.get(),
};
